"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ANCHOR_POINTS,
  NEW_GRADES,
  type NewGrade,
  newGradesFor,
  OLD_GRADES,
  type OldGrade,
  oldGradesFor,
  type PassLevel,
  passLevel,
  summariseResults,
} from "@/lib/tool-kit/intl-grades/gcse-grades";

/** English strings. A translated content file overrides these through `ui`. */
export const GCSE_GRADE_CONVERTER_STRINGS = {
  modeLabel: "Convert",
  modes: {
    "to-old": "9–1 grade → A*–G grade",
    "to-new": "A*–G grade → 9–1 grade",
  },
  newGradeLabel: "Your 9–1 grade",
  oldGradeLabel: "Your A*–G grade",
  ungraded: "U (ungraded)",
  results: "Result",
  comparableOld: "Comparable A*–G grade",
  comparableNew: "Comparable 9–1 grade",
  either: "{first} or {second}",
  passLevel: "DfE pass level",
  passLevels: {
    strong: "Strong pass (5 or above)",
    standard: "Standard pass (4)",
    below: "Below a standard pass",
  },
  anchorNote:
    "An anchor point: Ofqual aligns the bottom of grade {newGrade} with the bottom of grade {oldGrade}.",
  approxNote:
    "Only 7/A, 4/C and 1/G are fixed anchor points. Every other pairing is approximate: the scales are not directly equivalent.",
  resultsHeading: "Count your 9–1 results",
  resultsLabel: "Your grades, separated by spaces or commas",
  resultsPlaceholder: "7 6 6 5 4 4 3",
  atLeast4: "At grade 4 or above",
  atLeast5: "At grade 5 or above",
  atLeast7: "At grade 7 or above",
  ofTotal: "of {total} graded",
  ignored: "Not read as a 9–1 grade: {tokens}",
  emptyResults: "Type your 9–1 grades to count them.",
  caveat:
    "This is the England GCSE scale. Most GCSEs in Wales and Northern Ireland are still graded A* to G.",
  copyResult: "Copy result",
  copied: "Copied!",
  summaryToOld: "GCSE grade {grade} is comparable to {old}",
  summaryToNew: "GCSE grade {old} is comparable to {grade}",
} as const;

type Strings = typeof GCSE_GRADE_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type Mode = keyof Strings["modes"];

const MODES: readonly Mode[] = ["to-old", "to-new"];

const newLabel = (grade: NewGrade, strings: Strings, fmt: Format) =>
  grade === 0 ? strings.ungraded : fmt(grade);

const joinGrades = (grades: readonly string[], strings: Strings): string =>
  grades.length === 2
    ? t(strings.either, { first: grades[0] ?? "", second: grades[1] ?? "" })
    : (grades[0] ?? "");

function lowestPassLevel(grades: readonly NewGrade[]): PassLevel {
  return passLevel(Math.min(...grades) as NewGrade);
}

function AnchorLine({
  anchor,
  strings,
  fmt,
}: {
  anchor: readonly [NewGrade, OldGrade] | undefined;
  strings: Strings;
  fmt: Format;
}) {
  return (
    <p className="text-muted-foreground text-sm">
      {anchor
        ? t(strings.anchorNote, {
            newGrade: fmt(anchor[0]),
            oldGrade: anchor[1],
          })
        : strings.approxNote}
    </p>
  );
}

function ResultsCounter({ strings, fmt }: { strings: Strings; fmt: Format }) {
  const [raw, setRaw] = useState("");
  const summary = summariseResults(raw);
  const total = summary.grades.length;
  const totalText = t(strings.ofTotal, { total: fmt(total) });
  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.resultsHeading}</h3>
      <TextField
        id="gcse-results"
        label={strings.resultsLabel}
        onChange={setRaw}
        placeholder={strings.resultsPlaceholder}
        value={raw}
      />
      <div aria-live="polite" className="space-y-3">
        {total === 0 ? (
          <Notice>{strings.emptyResults}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard
                label={`${strings.atLeast4} ${totalText}`}
                value={fmt(summary.atLeast4)}
              />
              <StatCard
                label={`${strings.atLeast5} ${totalText}`}
                value={fmt(summary.atLeast5)}
              />
              <StatCard
                label={`${strings.atLeast7} ${totalText}`}
                value={fmt(summary.atLeast7)}
              />
            </StatGrid>
            {summary.ignored.length > 0 ? (
              <p className="text-muted-foreground text-sm">
                {t(strings.ignored, { tokens: summary.ignored.join(", ") })}
              </p>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

export default function GcseGradeConverterWidget() {
  const strings = useToolUi(GCSE_GRADE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("to-old");
  const [newGrade, setNewGrade] = useState<string>("7");
  const [oldGrade, setOldGrade] = useState<OldGrade>("A");

  const grade = Number(newGrade) as NewGrade;
  const oldMatches = oldGradesFor(grade);
  const newMatches = newGradesFor(oldGrade);
  const fromNew = mode === "to-old";
  const anchor = ANCHOR_POINTS.find(([anchorNew, anchorOld]) =>
    fromNew ? anchorNew === grade : anchorOld === oldGrade
  );
  const level = lowestPassLevel(fromNew ? [grade] : newMatches);
  const answer = fromNew
    ? joinGrades(oldMatches, strings)
    : joinGrades(
        newMatches.map((value) => newLabel(value, strings, fmt)),
        strings
      );
  const summary = fromNew
    ? t(strings.summaryToOld, {
        grade: newLabel(grade, strings, fmt),
        old: answer,
      })
    : t(strings.summaryToNew, { old: oldGrade, grade: answer });

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.modeLabel}
          name="gcse-mode"
          onChange={setMode}
          options={MODES.map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        {fromNew ? (
          <SelectField
            id="gcse-new"
            label={strings.newGradeLabel}
            onChange={setNewGrade}
            options={NEW_GRADES.map((value) => ({
              value: String(value),
              label: newLabel(value, strings, fmt),
            }))}
            value={newGrade}
          />
        ) : (
          <SelectField
            id="gcse-old"
            label={strings.oldGradeLabel}
            onChange={setOldGrade}
            options={OLD_GRADES.map((value) => ({
              value,
              label: value === "U" ? strings.ungraded : value,
            }))}
            value={oldGrade}
          />
        )}
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={fromNew ? strings.comparableOld : strings.comparableNew}
            value={answer}
          />
          <StatCard
            label={strings.passLevel}
            value={strings.passLevels[level]}
          />
        </StatGrid>
        <AnchorLine anchor={anchor} fmt={fmt} strings={strings} />
        <p className="text-muted-foreground text-sm">{strings.caveat}</p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          small
          text={summary}
        />
      </section>

      <ResultsCounter fmt={fmt} strings={strings} />
    </div>
  );
}
